/**
 * Auto-update hook for APK (Capacitor/WebView) deployments.
 *
 * On startup, fetches /android/version.json from the server and compares
 * versionCode against the embedded current version. If a newer version exists,
 * shows a toast notification (or forces redirect for critical updates).
 *
 * For PWA (web) users, updates are handled by the Service Worker —
 * this hook only activates on native Android / Sunmi WebView.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { devLog } from '@/lib/logger';
import { formatBytes } from '@/lib/format-bytes';
import { shouldStartWebUpdate } from '@/lib/update-policy';
import { isWebUpdateShown, markWebUpdateShown } from '@/lib/guided-update/session';

// ─── Current embedded version (fallback si le bridge natif absent) ─────
// Le bridge Android VisionPOSUpdater expose getCurrentVersion() /
// getCurrentVersionCode() qui lisent BuildConfig (source de vérité).
// On utilise ces valeurs en priorité, les constantes ne servent que de
// fallback PWA / dev.
const FALLBACK_VERSION_CODE = 49;
const FALLBACK_VERSION_NAME = '1.5.9.15';

function getNativeCurrentVersion(): { code: number; name: string } {
  try {
    const updater = (window as any).VisionPOSUpdater;
    if (updater?.getCurrentVersionCode && updater?.getCurrentVersion) {
      const code = Number(updater.getCurrentVersionCode());
      const name = String(updater.getCurrentVersion());
      if (!isNaN(code) && code > 0 && name) return { code, name };
    }
  } catch {
    /* bridge absent → fallback */
  }
  // Essayer aussi les flags injectés par MainActivity
  try {
    const w = window as any;
    if (
      typeof w.__VISIONPOS_APP_VERSION_CODE === 'number' &&
      typeof w.__VISIONPOS_APP_VERSION === 'string'
    ) {
      return { code: w.__VISIONPOS_APP_VERSION_CODE, name: w.__VISIONPOS_APP_VERSION };
    }
  } catch {}
  return { code: FALLBACK_VERSION_CODE, name: FALLBACK_VERSION_NAME };
}

const { code: CURRENT_VERSION_CODE, name: CURRENT_VERSION_NAME } = getNativeCurrentVersion();

// ─── Remote version manifest URL ─────────────────────────────
const VERSION_URL = 'https://pos.visionpos.fr/android/version.json';

// ─── Check interval: once per session + every 4 hours ────────
const CHECK_INTERVAL_MS = 4 * 60 * 60 * 1000;

// ─── Dismiss persistence ────────────────────────────────────
// Persiste en localStorage pour éviter que le toast revienne en boucle
// après chaque navigation vers le Dashboard.
const DISMISS_KEY = 'vp_update_dismissed';
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h avant de re-proposer

function isDismissed(versionCode: number): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const { code, ts } = JSON.parse(raw);
    // Dismiss valide si c'est le même versionCode et < 24h
    if (code === versionCode && Date.now() - ts < DISMISS_COOLDOWN_MS) return true;
  } catch {}
  return false;
}

function persistDismiss(versionCode: number): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ code: versionCode, ts: Date.now() }));
  } catch {}
}

// Garde anti-doublon : un seul toast par session JS, partagée avec la mise à jour guidée
let updaterListenerAttached = false;

// Suit les events 'vp-updater' de l'APK : progression du toast, navigateur système si échec.
function attachUpdaterEventListener(versionName: string) {
  if (updaterListenerAttached) return;
  updaterListenerAttached = true;

  const handler = (e: Event) => {
    const detail = ((e as CustomEvent).detail || {}) as {
      type?: string;
      percent?: number;
      downloaded?: number;
      total?: number;
      fileName?: string;
      reason?: string | number;
      fallbackUrl?: string;
    };
    const t = detail.type;

    if (t === 'download-start') {
      armInstallWatchdog(versionName);
      toast.loading(`Mise à jour v${versionName} — téléchargement 0%`, {
        id: 'auto-update',
        description: detail.fileName || 'Téléchargement…',
        duration: Infinity,
      });
    } else if (t === 'download-progress') {
      armInstallWatchdog(versionName);
      const pct = typeof detail.percent === 'number' && detail.percent >= 0 ? detail.percent : null;
      const desc =
        pct !== null
          ? `${pct}% — ${formatBytes(detail.downloaded || 0)} / ${formatBytes(detail.total || 0)}`
          : `${formatBytes(detail.downloaded || 0)} téléchargés`;
      toast.loading(`Mise à jour v${versionName} — ${pct !== null ? pct + '%' : 'en cours'}`, {
        id: 'auto-update',
        description: desc,
        duration: Infinity,
      });
    } else if (t === 'download-complete') {
      // Démarre un watchdog 60s : si pas d'install-intent / install-start après,
      // on dismiss le toast (UI bloquée à 100%) au lieu de rester stuck infini.
      armInstallWatchdog(versionName, 60_000);
      toast.loading(`Mise à jour v${versionName} — installation…`, {
        id: 'auto-update',
        description: 'Appuyez sur "Installer" si Android le demande',
        duration: Infinity,
      });
    } else if (t === 'install-intent' || t === 'install-start') {
      // L'install démarre côté OS — désarme le watchdog (l'app va se kill juste après).
      clearInstallWatchdog();
      toast.loading(`Mise à jour v${versionName} — installation…`, {
        id: 'auto-update',
        description: "L'app va redémarrer",
        duration: Infinity,
      });
    } else if (t === 'install-waiting' || t === 'install-session' || t === 'install-refused') {
      // L'installation attend la caisse inactive ou passe par Android : pas de redémarrage forcé.
      clearInstallWatchdog();
      toast.dismiss('auto-update');
    } else if (t === 'download-failed') {
      clearInstallWatchdog();
      toast.dismiss('auto-update');
      toast.error(`Échec téléchargement (${detail.reason || 'inconnu'}) — ouverture de la page`, {
        duration: 8000,
      });
      const url = detail.fallbackUrl || 'https://pos.visionpos.fr/android/';
      try {
        window.open(url, '_blank', 'noopener');
      } catch {}
    }
  };
  window.addEventListener('vp-updater', handler);
}

// Watchdog UI : si l'install ne démarre pas après le download-complete, on dismiss le toast
// et on kill l'app (System.exit côté natif) pour repartir d'un état propre.
let installWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
const KILL_ATTEMPTS_KEY = 'vp_install_kill_attempts';
const KILL_ATTEMPTS_TS_KEY = 'vp_install_kill_attempts_ts';
const KILL_MAX_ATTEMPTS = 3;
const KILL_RESET_AFTER_MS = 10 * 60 * 1000; // 10 min : reset compteur si pas de re-tentative

function getKillAttempts(): number {
  try {
    const ts = parseInt(localStorage.getItem(KILL_ATTEMPTS_TS_KEY) || '0', 10);
    if (ts && Date.now() - ts > KILL_RESET_AFTER_MS) {
      localStorage.removeItem(KILL_ATTEMPTS_KEY);
      localStorage.removeItem(KILL_ATTEMPTS_TS_KEY);
      return 0;
    }
    return parseInt(localStorage.getItem(KILL_ATTEMPTS_KEY) || '0', 10);
  } catch {
    return 0;
  }
}
function incKillAttempts(): number {
  const n = getKillAttempts() + 1;
  try {
    localStorage.setItem(KILL_ATTEMPTS_KEY, String(n));
    localStorage.setItem(KILL_ATTEMPTS_TS_KEY, String(Date.now()));
  } catch {}
  return n;
}

function armInstallWatchdog(versionName: string, timeoutMs = 120_000) {
  clearInstallWatchdog();
  installWatchdogTimer = setTimeout(() => {
    toast.dismiss('auto-update');
    const remoteOps = (window as any).VisionPOSRemoteOps;
    const attempts = getKillAttempts();

    if (remoteOps?.killTask && attempts < KILL_MAX_ATTEMPTS) {
      const next = incKillAttempts();
      toast.loading(
        `Installation v${versionName} bloquée — redémarrage auto (${next}/${KILL_MAX_ATTEMPTS})…`,
        { duration: 3000, description: "L'application va se relancer dans 2 secondes" },
      );
      setTimeout(() => {
        try {
          devLog(`[AutoUpdate] Watchdog → killTask (attempt ${next}/${KILL_MAX_ATTEMPTS})`);
          remoteOps.killTask();
        } catch (e) {
          console.warn('[AutoUpdate] killTask failed', e);
        }
      }, 2000);
    } else {
      // Bridge indisponible OU max kills atteint → message manuel
      toast.error(
        `Installation v${versionName} bloquée — contactez le support si l'app reste figée`,
        { duration: 10_000 },
      );
    }
  }, timeoutMs);
}
function clearInstallWatchdog() {
  if (installWatchdogTimer) {
    clearTimeout(installWatchdogTimer);
    installWatchdogTimer = null;
  }
}

interface VersionManifest {
  versionName: string;
  versionCode: number;
  apkUrl: string;
  releaseNotes: string;
  minAndroidSdk: number;
  updatedAt: string;
  forceUpdate: boolean;
  silent?: boolean;
}

interface UseAutoUpdateReturn {
  updateAvailable: boolean;
  remoteVersion: VersionManifest | null;
  checking: boolean;
  triggerUpdate: () => void;
  dismissUpdate: () => void;
  checkNow: () => Promise<void>;
}

function isNativeAndroid(): boolean {
  try {
    // VisionPOS APK shell (flag injected by MainActivity.java)
    if ((window as any).__VISIONPOS_ANDROID === true) return true;
    // Capacitor native
    if (
      typeof (window as any).Capacitor !== 'undefined' &&
      (window as any).Capacitor?.isNativePlatform?.() === true
    ) {
      return true;
    }
    // Sunmi WebView (APK shell with JS bridge)
    if (
      typeof (window as any).SunmiPrinter !== 'undefined' ||
      typeof (window as any).SunmiInnerPrinter !== 'undefined'
    )
      return true;
    // Android WebView with injected bridge
    if (typeof (window as any).NativePrinter !== 'undefined') return true;
    // Generic Android WebView
    const ua = navigator.userAgent || '';
    if (ua.includes('wv') && /Android/i.test(ua)) return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * Déclenche le téléchargement APK de manière compatible WebView.
 * Priorité :
 * 1. Bridge natif VisionPOSUpdater.downloadAndInstall(url) — download dans app + intent install
 * 2. Android DownloadManager via bridge — déclenche le téléchargement système
 * 3. Navigateur système Android (intent VIEW) — le SEUL moyen fiable d'installer un APK
 *    depuis une WebView. Le navigateur système gère les permissions "sources inconnues"
 *    et le PackageInstaller.
 * 4. Capacitor Browser plugin (ouvre dans le navigateur système)
 * 5. Fallback: page de téléchargement (pas le lien direct, pour éviter les blocages)
 */
function triggerApkDownload(apkUrl: string): void {
  try {
    // 1. Bridge natif auto-updater (AppUpdater.java injecté par MainActivity)
    const updater = (window as any).VisionPOSUpdater;
    if (updater?.downloadAndInstall) {
      devLog('[AutoUpdate] Using native VisionPOSUpdater bridge');
      updater.downloadAndInstall(apkUrl);
      return;
    }
  } catch (e) {
    console.warn('[AutoUpdate] Native updater failed:', e);
  }

  try {
    // 2. Android DownloadManager via bridge
    const dm = (window as any).VisionPOSDownloader;
    if (dm?.download) {
      devLog('[AutoUpdate] Using VisionPOSDownloader bridge');
      dm.download(apkUrl, 'VisionPos.apk');
      return;
    }
  } catch (e) {
    console.warn('[AutoUpdate] DownloadManager bridge failed:', e);
  }

  // 3. Ouvrir dans le navigateur système Android via Intent
  // C'est le seul moyen fiable : le navigateur gère le téléchargement APK,
  // les permissions "sources inconnues", et l'intent d'installation.
  // NE PAS utiliser fetch+blob dans la WebView — Android bloque l'installation.
  const downloadPageUrl = 'https://pos.visionpos.fr/android/';

  try {
    // 3a. Intent natif Android (via bridge si disponible)
    const intent = (window as any).VisionPOSIntent;
    if (intent?.openInBrowser) {
      devLog('[AutoUpdate] Opening in system browser via native intent');
      intent.openInBrowser(downloadPageUrl);
      return;
    }
  } catch (e) {
    console.warn('[AutoUpdate] Native intent failed:', e);
  }

  try {
    // 3b. Capacitor Browser plugin (ouvre dans le navigateur système)
    if ((window as any).Capacitor?.Plugins?.Browser?.open) {
      (window as any).Capacitor.Plugins.Browser.open({ url: downloadPageUrl });
      devLog('[AutoUpdate] Opened download page via Capacitor Browser');
      return;
    }
  } catch (e) {
    console.warn('[AutoUpdate] Capacitor Browser failed:', e);
  }

  // 4. Fallback : window.open vers la page de téléchargement
  // Préférer la page HTML qui explique comment installer, plutôt que le lien
  // APK direct qui sera bloqué par la WebView.
  devLog('[AutoUpdate] Fallback: opening download page in new window');
  try {
    window.open(downloadPageUrl, '_blank', 'noopener');
  } catch {
    // Dernier recours : lien direct
    const link = document.createElement('a');
    link.href = downloadPageUrl;
    link.target = '_blank';
    link.rel = 'noopener';
    link.click();
  }

  toast.info("Téléchargez la mise à jour depuis la page qui s'ouvre", {
    description: 'Si Android bloque : Paramètres → Sécurité → Sources inconnues → Autoriser',
    duration: 10000,
  });
}

export function useAutoUpdate(): UseAutoUpdateReturn {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState<VersionManifest | null>(null);
  const [checking, setChecking] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkForUpdate = useCallback(async () => {
    // Only check on native Android / WebView
    if (!isNativeAndroid()) return;

    setChecking(true);
    try {
      // Sprint remote-ops G : on interroge get_update_target pour rollback/staged rollout.
      // Fallback vers version.json (signed static) si la RPC échoue (offline, RLS, etc.).
      let manifest: VersionManifest | null = null;
      let rollbackMode = false;

      // Kill switch updates.autoupdate — desactive completement la verif/install auto
      try {
        const { data: locked } = await supabase.rpc('is_feature_locked', {
          p_key: 'updates.autoupdate',
        });
        if (locked) {
          devLog('[AutoUpdate] auto-update locked by kill switch, skipping');
          setUpdateAvailable(false);
          setRemoteVersion(null);
          return;
        }
      } catch {
        /* ignore — fail open: si le check echoue on continue */
      }

      try {
        // Récupérer device_id + tenant_id pour activer les overrides per-device
        // dans get_update_target (tenant_device_overrides). Permet de pousser
        // un APK pilote sur un seul D3 sans toucher la flotte.
        const { getOrCreateDeviceId } = await import('@/hooks/useDeviceHeartbeat');
        const deviceId = getOrCreateDeviceId();
        let tenantId: string | null = null;
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          if (user) {
            const { data: prof } = await supabase
              .from('profiles')
              .select('tenant_id')
              .eq('user_id', user.id)
              .maybeSingle();
            tenantId = (prof as { tenant_id?: string } | null)?.tenant_id ?? null;
          }
        } catch {
          /* fail open */
        }

        const { data: target, error } = await supabase.rpc('get_update_target', {
          p_current_code: CURRENT_VERSION_CODE,
          p_channel: 'stable',
          p_device_id: deviceId,
          p_tenant_id: tenantId,
        });
        if (!error && Array.isArray(target) && target.length > 0) {
          const row: any = target[0];
          if (
            row?.action === 'update' ||
            row?.action === 'rollback' ||
            row?.action === 'force-update' ||
            row?.action === 'override'
          ) {
            const isForce =
              row.action === 'rollback' ||
              row.action === 'force-update' ||
              row.force_update === true;
            manifest = {
              versionName: row.target_name,
              versionCode: row.target_code,
              apkUrl: row.apk_url,
              releaseNotes: row.release_notes || '',
              minAndroidSdk: 24,
              updatedAt: new Date().toISOString(),
              forceUpdate: isForce,
            };
            rollbackMode = row.action === 'rollback';
          }
        }
      } catch (e) {
        console.warn('[AutoUpdate] get_update_target RPC failed, falling back to version.json', e);
      }

      if (!manifest) {
        const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        manifest = await res.json();
      }

      if (manifest && (rollbackMode || manifest.versionCode > CURRENT_VERSION_CODE)) {
        setRemoteVersion(manifest);
        setUpdateAvailable(!manifest.silent);

        const shouldAutoUpdate = shouldStartWebUpdate(
          manifest,
          isDismissed(manifest.versionCode),
          isWebUpdateShown(),
        );

        if (shouldAutoUpdate) {
          markWebUpdateShown();
          toast.loading(`Mise à jour v${manifest.versionName} — préparation…`, {
            id: 'auto-update',
            description: 'Téléchargement en cours',
            duration: Infinity,
          });

          // Écoute les events du bridge natif pour montrer un vrai progress + fallback
          attachUpdaterEventListener(manifest.versionName);

          try {
            triggerApkDownload(manifest.apkUrl);
          } catch (err) {
            console.error('[AutoUpdate] triggerApkDownload threw:', err);
            toast.dismiss('auto-update');
            toast.error('Échec mise à jour — ouverture de la page de téléchargement', {
              duration: 8000,
            });
            try {
              window.open('https://pos.visionpos.fr/android/', '_blank', 'noopener');
            } catch {}
          }
          persistDismiss(manifest.versionCode);
        }
      } else {
        setUpdateAvailable(false);
        setRemoteVersion(null);
      }
    } catch (err) {
      console.warn('[AutoUpdate] Check failed:', err);
      // Silently fail — don't bother the user
    } finally {
      setChecking(false);
    }
  }, []);

  const triggerUpdate = useCallback(() => {
    if (remoteVersion?.apkUrl) {
      triggerApkDownload(remoteVersion.apkUrl);
    } else {
      // Fallback: open the install page
      triggerApkDownload('https://pos.visionpos.fr/android/');
    }
  }, [remoteVersion]);

  const dismissUpdate = useCallback(() => {
    if (remoteVersion) persistDismiss(remoteVersion.versionCode);
    markWebUpdateShown();
    setUpdateAvailable(false);
  }, [remoteVersion]);

  useEffect(() => {
    // Initial check after a short delay (let the app load first)
    const timeout = setTimeout(() => {
      checkForUpdate();
    }, 5000);

    // Periodic checks
    intervalRef.current = setInterval(checkForUpdate, CHECK_INTERVAL_MS);

    return () => {
      clearTimeout(timeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkForUpdate]);

  return {
    updateAvailable,
    remoteVersion,
    checking,
    triggerUpdate,
    dismissUpdate,
    checkNow: checkForUpdate,
  };
}

/** Export current version for use in settings/about screens */
export const APK_VERSION_CODE = CURRENT_VERSION_CODE;
export const APK_VERSION_NAME = CURRENT_VERSION_NAME;
